"use client";

import React, { FC } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { buildOpenReplayLabel } from "@utils/openreplay";

/**
 * 推荐问题数据结构
 * @interface Question
 */
export interface Question {
  /** 问题唯一标识 */
  id: string;
  /** 问题文本内容 */
  question: string;
  /** 推荐问题的 source_id（用于标识推荐问题的来源） */
  source_id?: string;
  /** 推荐问题的 scope（用于标识推荐问题的类型，如 TOKEN_CHAT_REC、STOCK_CHAT_REC） */
  scope?: string;
  /** 推荐问题的 prompt（用于标识推荐问题的提示词） */
  prompt?: string;
  /** 数据跟踪范围 */
  dataTrackingScope?: string;
  // 是否高亮显示，通常用于区分当前点击的问题
  isHighlight?: boolean;
  /**
   * 实际发给 Ed 的文本（EDG-543 广告承接：`[](referenceType=…)\n{问题原文}`）。
   * 缺省即与 {@link question} 相同 —— 存量调用方不传，行为不变。
   * 分开两个字段是因为**展示的是问题原文、发送的是协议行**，二者不能混。
   */
  sendText?: string;
  /** EDG-591 Chat 要数据槽：英文原句与题组类目（埋点用）；普通推荐问题不带 */
  connectSlot?: { prompt: string; category: string };
  /**
   * 点击不走通用「填输入框 / 直接发送」，由对应入口接管（EDG-561）。
   * 目前仅 `etf_overlap`：走入口分流（有持仓直达 Chat，无持仓开引导弹窗）。
   */
  intent?: "etf_overlap";
}

/**
 * QuestionItem 单个问题项组件属性
 * @interface QuestionItemProps
 */
export interface QuestionItemProps {
  /** 问题数据 */
  question: Question;
  /** 点击问题时的回调函数 */
  onClick: (question: Question) => void;
  /** 自定义样式类名 */
  className?: string;
  textClassName?: string;
  /** OpenReplay v2 DOM label (`Scene: Domain: Action: OriginalText`) */
  openReplayLabel?: string;
  /**
   * 额外的 `data-or-*` 声明式埋点属性，原样落到按钮上（`data-or-param-*` 必须与 `data-or-view` / `data-or-event`
   * 同元素，所以只能由 QuestionItem 内部展开到这个 `<button>`）。
   */
  dataProps?: Record<`data-${string}`, string>;
}
/**
 * QuestionItem 组件
 * 单个推荐问题的展示项，包含图标和问题文本
 *
 * @param props QuestionItemProps
 * @returns React.ReactElement
 */
export const QuestionItem: FC<QuestionItemProps> = ({
  question,
  onClick,
  className,
  textClassName,
  openReplayLabel,
  dataProps,
}) => {
  /**
   * 处理点击事件
   */
  const handleClick = () => {
    onClick(question);
  };

  const replayLabel =
    openReplayLabel ??
    buildOpenReplayLabel("ChatBox", "QuestionSuggestion", "Click");

  return (
    <button
      data-openreplay-label={replayLabel}
      id="btn-question-item"
      type="button"
      onClick={handleClick}
      className={cn(
        "flex items-center gap-2 w-full p-2 rounded-2 bg-transparent hover:bg-background_white_overlay5 transition-colors cursor-pointer text-left",
        className,
      )}
      data-tracking-scope={question.dataTrackingScope}
      aria-label={`选择问题: ${question.question}`}
      {...dataProps}
    >
      {/* 图标 */}
      <Icon
        name="ai-magic-wand"
        size={20}
        className="text-content_brand_primary"
      />

      {/* 问题文本 - 单行显示，超长省略 */}
      <span
        className={cn(
          "body_m text-content_primary truncate flex-1",
          textClassName,
        )}
      >
        {question.question}
      </span>
    </button>
  );
};
